import type { IPublicClientApplication, SilentRequest } from '@azure/msal-browser'

/**
 * Qué hacer cuando la renovación silenciosa del token falla: la sesión venció (la pestaña se
 * quedó abierta sin usarse), Entra pide interacción, o el navegador bloquea el iframe oculto
 * con que MSAL intenta renovar (Safari, cookies de terceros).
 *
 * Antes se caía a una VENTANA EMERGENTE. Con la página ya cargada nadie hizo clic, así que el
 * navegador la bloqueaba, y la pantalla se quedaba con «No se pudo validar tu sesión… cierra
 * sesión y entra de nuevo»: el usuario tenía que hacer a mano justo lo que la SPA podía hacer
 * sola. Ahora se redirige a Entra con la misma petición. Si la sesión del CIAM sigue viva,
 * vuelve en un parpadeo con el token nuevo; si no, cae en la pantalla de inicio de sesión. En
 * los dos casos MSAL regresa a la página en que estaba.
 *
 * Contra el bucle: si ya se redirigió hace menos de cinco minutos y la renovación vuelve a
 * fallar, otra vuelta no lo va a arreglar. Se cierra la sesión (logoutRedirect limpia el caché
 * de MSAL) y el usuario aterriza en la portada, sin sesión a medias.
 *
 * Los errores de red NO redirigen: sin conexión, Entra tampoco responde, y el mensaje de «revisa
 * tu conexión» es lo que sirve.
 *
 * Vive en su propio módulo por lo mismo que erroresDeAutenticacion.ts: los dos módulos de
 * autenticación están simulados por completo en pruebas/configuracion.ts.
 */

const VENTANA_CONTRA_BUCLE_MS = 5 * 60 * 1000

/** Códigos de MSAL que significan «no hubo red», no «la sesión no sirve». */
const CODIGOS_DE_RED = new Set(['no_network_connectivity', 'post_request_failed', 'get_request_failed'])

function esErrorDeRed(error: unknown): boolean {
  const codigo = typeof error === 'object' && error !== null ? (error as { errorCode?: unknown }).errorCode : undefined
  return typeof codigo === 'string' && CODIGOS_DE_RED.has(codigo)
}

/** Promesa que nunca se resuelve: la página está por irse a Entra y nadie debe pintar un error. */
const mientrasSeVa = () => new Promise<string>(() => {})

/**
 * Devuelve la función que obtiene el token de `instancia`. `clave` es la marca en
 * sessionStorage con la hora del último redirect; una por instancia de MSAL, para que la
 * sesión de la consola de plataforma no se mezcle con la del despacho.
 */
export function crearRenovadorDeToken(instancia: IPublicClientApplication, clave: string) {
  // Varias pantallas piden token a la vez (tablero, notificaciones…). Un solo redirect: dos
  // casi simultáneos chocan con «interaction_in_progress» en MSAL.
  let redireccionEnCurso: Promise<string> | null = null

  async function reautenticar(peticion: SilentRequest): Promise<string> {
    const ultima = Number(sessionStorage.getItem(clave) ?? 0)
    if (Date.now() - ultima < VENTANA_CONTRA_BUCLE_MS) {
      sessionStorage.removeItem(clave)
      await instancia.logoutRedirect({ account: peticion.account })
      return mientrasSeVa()
    }

    sessionStorage.setItem(clave, String(Date.now()))
    try {
      await instancia.acquireTokenRedirect(peticion)
    } catch (fallo) {
      // No se salió de la página: esto no cuenta como un intento, o el siguiente fallo
      // cerraría la sesión sin haber probado nunca el redirect.
      sessionStorage.removeItem(clave)
      throw fallo
    }
    return mientrasSeVa()
  }

  return async function obtener(peticion: SilentRequest): Promise<string> {
    try {
      const resultado = await instancia.acquireTokenSilent(peticion)
      sessionStorage.removeItem(clave)
      return resultado.accessToken
    } catch (error) {
      if (esErrorDeRed(error)) throw error

      redireccionEnCurso ??= reautenticar(peticion).catch((fallo: unknown) => {
        // Si ni siquiera se pudo salir hacia Entra, se suelta el guard y el error llega a la
        // pantalla traducido (api.ts → erroresDeAutenticacion.ts).
        redireccionEnCurso = null
        throw fallo
      })
      return redireccionEnCurso
    }
  }
}
